import React from "react";
import { Helmet } from "react-helmet";
import { ShareSection } from "../../components/template";

// Hashtag section
const HashtagSection = ({ tags }) => (
  <div className="mt-6 flex flex-wrap md:gap-3 gap-1 justify-center md:mb-6 mb-3">
    {tags.map((tag, index) => (
      <span
        key={index}
        className="bg-red-700 bg-opacity-10 text-red-700 px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all duration-300 m-1 mb-2 hover:bg-red-700 hover:text-white"
      >
        {tag}
      </span>
    ))}
  </div>
);

const Blog17 = () => {
  const hashtags = [
    "#emotionalintelligence",
    "#innerpeace",
    "#reactionvsresponse",
    "#selfawareness",
    "#personalgrowth",
    "#mindfulness",
    "#selfcontrol",
    "#consciousliving",
    "#ParamLife",
  ];

  return (
    <>
      <Helmet>
        <title>
          Master This Habit — And People Will Stop Triggering You
        </title>
        <meta
          name="description"
          content="Learn the simple emotional mastery habit that helps you stop reacting impulsively and start responding with calm confidence."
        />
      </Helmet>

      <article className="max-w-4xl mx-auto mt-10 px-6 py-4 leading-relaxed text-gray-800">
        <h1 className="text-secondary text-left text-3xl font-semibold mb-8">
          Master This Habit — And People Will Stop Triggering You
        </h1>
   <img
          src="/assets/knowyourself/blogs/blog17.png"
          alt="7 Things Our Grandparents Did Right (But We Ruined)"
          className="w-[80%] max-w-xl rounded-2xl shadow-md mx-auto mb-10 ml-10"
        />

        <p className="text-center italic text-gray-600 mb-6">
          The real secret to staying calm when life (and people) test you.
        </p>

        {/* Intro */}
        <section className="space-y-4 mb-8">
          <p>Let’s be honest…</p>

          <p>
            You’re not really angry because someone cut you off in traffic.
            <br />
            You’re not frustrated because your friend didn’t reply to your
            message.
            <br />
            You’re not even upset because life isn’t fair.
          </p>

          <p className="text-[#578d48] font-semibold">
            You’re triggered because you lost control of how you respond.
          </p>

          <p>
            Think about it.
            <br />
            You wake up peaceful… then one thing goes wrong — and boom, your
            entire mood is hijacked.
          </p>

          <p>
            It’s like your emotions have a remote control and everyone else
            knows the buttons.
          </p>

          <p className="text-[#578d48] font-semibold">
            But here’s what emotionally intelligent people have learned (and
            what most of us never do):
          </p>

          <p className="text-[#578d48] font-semibold">
            They’ve mastered the pause — the tiny moment between what happens
            and how they respond.
          </p>
        </section>

        {/* Core Habit */}
        <section className="space-y-4 mb-10">
          <h2 className="text-secondary text-2xl font-semibold">
            The Habit That Changes Everything
          </h2>

          <p>
            It’s not meditation. Not journaling. Not running off to the
            Himalayas.
          </p>

          <p className="text-[#578d48] font-semibold">
            It’s simply this —
            <br />
            Pause before you react.
          </p>

          <p>
            That’s it.
            <br />
            The 2-second delay that makes you look calm while everyone else is
            losing it.
          </p>

          <p>
            When someone says something hurtful — you pause.
            <br />
            When you see something triggering online — you pause.
            <br />
            When plans fall apart — you pause.
          </p>

          <p className="text-[#578d48]">
            Because that pause gives you power.
            <br />
            It’s where you choose response over reaction.
            <br />
            It’s where emotional maturity lives.
          </p>
        </section>

        {/* Real Life */}
        <section className="space-y-6 mb-10">
          <h2 className="text-secondary text-2xl font-semibold">
            Let’s Make This Real — Because Life Isn’t a Zen Retreat
          </h2>

          <p>
            Let’s see how this habit actually plays out in the messy, noisy real
            world.
          </p>

          <div>
            <h3 className="text-primary font-semibold">
              1. When You Fail at Something
            </h3>

            <p>
              You miss the promotion.
              <br />
              The launch flops.
              <br />
              Your plan collapses.
            </p>

            <p>Your mind instantly goes, “I’m not good enough.”</p>

            <p className="text-[#578d48]">Pause.</p>

            <p>Ask yourself, “Wait… what is this teaching me?”</p>

            <p className="text-[#578d48]">
              Because failure isn’t proof that you’re unworthy — it’s feedback
              that you’re learning.
            </p>

            <p>
              You either win or you gain wisdom.
            </p>

            <p className="text-[#578d48]">
              Emotionally intelligent people don’t beat themselves up.
              <br />
              They extract lessons, not guilt.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              2. When You Fall Sick
            </h3>

            <p>
              You had plans. You had momentum. Then — boom — body says, “Nope.”
            </p>

            <p>
              You feel useless, anxious, like you’re falling behind.
            </p>

            <p className="text-[#578d48]">Pause.</p>

            <p className="text-[#578d48]">
              Your body isn’t betraying you. It’s communicating.
            </p>

            <p>Ask, “What have I been ignoring?”</p>

            <p className="text-[#578d48]">
              Sometimes sickness is your body’s way of saying what your mouth
              won’t — “I need rest.”
            </p>

            <p className="text-[#578d48]">
              Stop fighting it. Heal with kindness.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              3. When Someone Treats You Badly
            </h3>

            <p>
              Someone talks behind your back.
              <br />
              Someone disrespects you.
              <br />
              Someone ghosts you.
            </p>

            <p>Your first instinct? Burn them down — mentally or emotionally.</p>

            <p className="text-[#578d48]">Pause.</p>

            <p className="text-[#578d48]">
              Remember: people’s behavior shows who they are, not who you are.
            </p>

            <p>
              If you mirror their energy, you let them control you twice — once
              through the hurt, and again through your reaction.
            </p>

            <p className="text-[#578d48]">
              Stay grounded. Stay in integrity.
              <br />
              Let their chaos stay theirs.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              4. When Social Media Triggers You
            </h3>

            <p>You scroll.</p>

            <p>
              Everyone’s doing better — or so it seems.
            </p>

            <p>
              Instantly, that voice whispers, “You’re behind. You’re not
              enough.”
            </p>

            <p className="text-[#578d48]">Pause.</p>

            <p>
              Close the app.
              <br />
              Take a breath.
            </p>

            <p className="text-[#578d48]">
              Remind yourself: nobody posts their breakdowns. Only their
              breakthroughs.
            </p>

            <p className="text-[#578d48]">
              Your journey is not a race — it’s a rhythm.
              <br />
              And you’re right on time.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              5. When Someone Disagrees With You
            </h3>

            <p>
              You express your truth.
              <br />
              Someone challenges it.
              <br />
              You feel defensive. You want to prove your point.
            </p>

            <p className="text-[#578d48]">Pause.</p>

            <p>Ask, “Do I want peace or do I want to win?”</p>

            <p className="text-[#578d48]">
              Emotionally intelligent people don’t need to dominate
              conversations.
            </p>

            <p className="text-[#578d48]">
              They understand — listening doesn’t mean agreeing.
            </p>

            <p className="text-[#578d48]">
              They choose inner calm over outer approval.
            </p>
          </div>
        </section>

        {/* Neuroscience */}
        <section className="space-y-4 mb-10">
          <h2 className="text-secondary text-2xl font-semibold">
            The Inner Mechanics of the Pause
          </h2>

          <p>
            Here’s the neuroscience behind it (in plain English):
          </p>

          <p>
            Your emotional brain (the amygdala) reacts in milliseconds.
            <br />
            Your rational brain (the prefrontal cortex) kicks in a little later.
          </p>

          <p className="text-[#578d48]">
            The pause — that 1-2 seconds — gives your rational brain time to
            catch up.
          </p>

          <p className="text-[#578d48]">
            That’s how you go from “Why the hell did I say that?”
            <br />
            to
            <br />
            “I handled that better than ever.”
          </p>
        </section>

        {/* Truth */}
        <section className="space-y-4 mb-10">
          <h2 className="text-secondary text-2xl font-semibold">
            The Truth Nobody Tells You
          </h2>

          <p>
            You can’t stop people from being rude.
            <br />
            You can’t stop bad days.
            <br />
            You can’t stop the noise of the world.
          </p>

          <p className="text-[#578d48]">
            But you can stop giving your peace away for free.
          </p>

          <p className="text-[#578d48]">
            Emotional intelligence isn’t about never feeling triggered.
            <br />
            It’s about noticing the trigger, taking a breath, and choosing your
            response.
          </p>

          <p className="text-[#578d48]">
            It’s not about being cold or detached — it’s about being conscious.
          </p>

          <p className="text-[#578d48]">
            Because that’s the only place where freedom exists.
          </p>
        </section>

        {/* Wisdom */}
        <section className="space-y-4 mb-10">
          <p className="text-[#578d48] font-semibold">Wisdom Lens:</p>

          <p className="text-[#578d48] italic">
            “Between stimulus and response, there is a space.
            <br />
            In that space lies our power to choose our response.
            <br />
            In our response lies our growth and our freedom.”
            <br />— Viktor Frankl
          </p>

          <p className="text-[#578d48]">
            That’s the entire game of emotional mastery.
            <br />
            The more you expand that space, the more peaceful your life becomes.
          </p>

          <p className="text-[#578d48] font-semibold">Modern Meaning:</p>

          <p className="text-[#578d48]">
            The world doesn’t get easier — you get stronger.
            <br />
            Master the pause, and people stop controlling your emotions.
          </p>

          <p className="text-[#578d48] font-semibold">Journal Prompt:</p>

          <p className="text-[#578d48] italic">
            “What triggers me most — and what can I choose to do differently the
            next time it happens?”
          </p>
        </section>

        <HashtagSection tags={hashtags} />
        <ShareSection />
      </article>
    </>
  );
};

export default Blog17;
